import React, { useEffect, useState } from 'react';
import { Box, Stack, Backdrop, CircularProgress, Alert } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { getOTBReport, getOTBMemorize, getCorpPcList, GetLegalNameorDbName } from '../../API/nimbleAPI';
import { Otbreportpayload } from '../../Payload/OTBReportPayload';
import dayjs from 'dayjs';
import quarterOfYear from "dayjs/plugin/quarterOfYear";


import Buttons from '../Report/Tools/Buttons';
import Tables from '../Report/Tables/Table';
import OTBReport from './Report/OTBReport';
import { COLORS } from '../../Components/colors';
dayjs.extend(quarterOfYear);

const KEYS_TO_RESET = [
  "TotalRooms", "TrnsRooms", "GrpRooms",
  "OTBRoomsSold", "OTBOcc", "OTBADR", "OTBRoomRev", "OTBRevPar",
  "PickUpRooms", "PickUpADR", "PickUpRev", "PickUpOcc",
  "BudgetRoomsSold", "BudgetOcc", "BudgetAdr", "BudgetRoomRev", "BudgetRevPar",
  "RoomsSoldVariance", "ADRVariance", "RoomRevenueVariance", "OccVariance",
  "LYRoomsSold", "LYOccupancy", "LYADR", "LYRoomRev", "LYRevPar",
  "LYRoomsSoldVariance", "LYADRVariance", "LYRoomRevenueVariance", "LYOccVariance"
];

const ReportFinalPage = () => {
  const location = useLocation();
  const [stateData, setStateData] = useState(location.state || null);
  const [loading, setLoading] = useState(() => {
    if (location.state && !Array.isArray(location.state.reportData)) {
      return true;
    }
    return false;
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (location.state) {
      const incomingState = location.state;
      setStateData(incomingState);
      // Fetch legal name setting if loaded from state
      const fetchSetting = async () => {
        try {
          const setting = await GetLegalNameorDbName();
          setStateData(prev => ({ ...prev, showLegalName: setting === true }));
        } catch (e) {
          console.error(e);
        }
      };
      fetchSetting();

      const hasReportData = Array.isArray(incomingState.reportData);
      if (!hasReportData) {
        const payload = incomingState.metaData || incomingState.reportPayload;
        if (payload) {
          const fetchReportFromState = async () => {
            setLoading(true);
            setError(null);
            try {
              const response = await getOTBReport(payload);
              let data = [];
              let totals = null;
              if (Array.isArray(response)) {
                data = response;
                if (response.length > 0) totals = response[0];
              } else if (response && typeof response === 'object') {
                if (Array.isArray(response.OTBReportList)) {
                  data = response.OTBReportList;
                } else {
                  const key = Object.keys(response).find(k => Array.isArray(response[k]));
                  if (key) data = response[key];
                }
                totals = response;
              }
              setStateData(prev => ({
                ...prev,
                reportData: data || [],
                totals: totals
              }));
            } catch (error) {
              console.error("Error fetching report from state payload:", error);
              setError("Failed to load report. Please try again.");
            } finally {
              setLoading(false);
            }
          };
          fetchReportFromState();
        }
      }
      return;
    }

    const params = new URLSearchParams(location.search);
    if (params.toString()) {
      const fetchReportFromUrl = async () => {
        setLoading(true);
        setError(null);
        try {
          const corpId = params.get('corporationID') || '0';
          const pcIdLst = params.get('PCID') || params.get('PCIdLst') || params.get('PCIdlist') || params.get('pcid');
          const fromDate = params.get('ShortFromDate') || params.get('fromDate');
          const toDate = params.get('ShortToDate') || params.get('toDate');
          const budget = params.get('budget') === 'true';
          const ly = params.get('ly') === 'true';
          const actual = params.get('actual') === 'true';
          const pickup = params.get('pickup');
          const column = params.get('column');
          const reportType = params.get('reportType');
          const viewby = params.get('viewby');
          const pickupof = params.get('pickupof');
          const pickupdates = params.get('pickupdates');
          const userId = params.get('userId') || params.get('userID');
          const menuId = params.get('menuId') || params.get('menuID');
          const urlInfo = params.get('urlInfo');

          let selectedRows = [];
          try {
            const corpList = await getCorpPcList("ALL");
            if (Array.isArray(corpList)) {
              const mappedRows = corpList.map((item) => ({
                id: `${item.PCID}_${item.CorporationID}`,
                corpId: item.CorporationID,
                pcId: item.PCID,
                corpName: item.CorporationName,
                legalName: item.PCName,
                brandType: item.PropertyType,
                service: item.Service,
                brand: item.Brand,
                pms: item.PMS,
              }));

              const urlCorpIDs = corpId.split(',').map(id => id.trim());
              const urlPcIDs = pcIdLst ? pcIdLst.split(',').map(id => id.trim().startsWith('0x') ? id.trim().slice(2) : id.trim()) : [];

              if (urlCorpIDs.length > 0 && urlCorpIDs[0] !== '0') {
                selectedRows = mappedRows.filter(r => urlCorpIDs.includes(r.corpId));
                if (urlPcIDs.length > 0) {
                  selectedRows = selectedRows.filter(r => urlPcIDs.includes(r.pcId));
                }
              }
            }
          } catch (error) {
            console.error("Error fetching corporation list:", error);
          }

          let showLegalNameVal = false;
          try {
            const setting = await GetLegalNameorDbName();
            showLegalNameVal = setting === true;
          } catch (e) { console.error(e); }

          let memorized = null;
          let viewTransactionRange = 'Next 30 Days';
          if (userId && menuId) {
            try {
              const res = await getOTBMemorize({ UserId: userId, MenuId: menuId });
              if (Array.isArray(res) && res.length > 0) memorized = res[0];
            } catch (e) { console.error(e); }
          }

          const payload = { ...Otbreportpayload };
          payload.CorporationIDLst = corpId;
          const formattedPcIdLst = pcIdLst ? pcIdLst.split(',').map(id => id.trim().startsWith('0x') ? id.trim().slice(2) : id.trim()).join(',') : "";
          payload.PCIdLst = formattedPcIdLst;
          payload.PCIdlist = formattedPcIdLst;

          if (memorized) {
            const viewTransTypeMap = {
              1: "Next 7 Days",
              2: "Next 14 Days",
              3: "Next 30 Days",
              4: "Next 45 Days",
              5: "Next 60 Days",
              6: "Next 90 Days (Next Quarter)",
              7: "Next 120 Days",
              8: "Next 6 Months",
              9: "Next Financial Quarter",
              10: "Next 9 Months",
              11: "Next 12 Months (1 Year Forecast)",
              12: "Custom Date Range"
            };
            if (viewTransTypeMap[memorized.ViewTransType]) {
              viewTransactionRange = viewTransTypeMap[memorized.ViewTransType];
            }
          }

          // Apply Memorized Dates if not in URL
          if (memorized && !fromDate && !toDate) {
            const today = dayjs();
            let start = today.startOf('day');
            let end = today.add(30, 'day').endOf('day');

            switch (memorized.ViewTransType) {
              case 1: end = today.add(7, 'day').endOf('day'); break;
              case 2: end = today.add(14, 'day').endOf('day'); break;
              case 3: end = today.add(30, 'day').endOf('day'); break;
              case 4: end = today.add(45, 'day').endOf('day'); break;
              case 5: end = today.add(60, 'day').endOf('day'); break;
              case 6: end = today.add(90, 'day').endOf('day'); break;
              case 7: end = today.add(120, 'day').endOf('day'); break;
              case 8: end = today.add(6, 'month').endOf('day'); break;
              case 9:
                start = today.add(1, 'quarter').startOf('quarter');
                end = today.add(1, 'quarter').endOf('quarter');
                break;
              case 10: end = today.add(9, 'month').endOf('day'); break;
              case 11: end = today.add(1, 'year').endOf('day'); break;
              case 12:
                if (memorized.CustomizeString) {
                  const fromMatch = memorized.CustomizeString.match(/%ShortFromDate:([^%]+)%/);
                  const toMatch = memorized.CustomizeString.match(/%ShortToDate:([^%]+)%/);
                  if (fromMatch) start = dayjs(fromMatch[1]);
                  if (toMatch) end = dayjs(toMatch[1]);
                }
                break;
              default: break;
            }
            payload.ShortFromDate = start.format("YYYY-MM-DD");
            payload.ShortToDate = end.format("YYYY-MM-DD");
          }

          if (fromDate) payload.ShortFromDate = dayjs(fromDate).format("YYYY-MM-DD");
          if (toDate) payload.ShortToDate = dayjs(toDate).format("YYYY-MM-DD");

          // Apply Memorized Pickup if not in URL
          if (memorized && !pickup) {
            payload.PickUpDays = memorized.PickUpdays;
          }

          if (pickup) {
            payload.PickUpDays = parseInt(pickup, 10);
          }

          // RESET all flags
          KEYS_TO_RESET.forEach(key => { payload[key] = false; });

          const showOTB = actual || !pickup;

          if (reportType) {
            payload.TotalRooms = true;
            ['OTBRoomsSold', 'OTBOcc', 'OTBADR', 'OTBRoomRev', 'OTBRevPar'].forEach(k => payload[k] = true);
            switch (reportType) {
              case '1': // vs 1 Day Pickup
                payload.PickUpDays = 1;
                ['PickUpRooms', 'PickUpADR', 'PickUpRev', 'PickUpOcc'].forEach(k => payload[k] = true);
                break;
              case '2': // vs 3 Days Pickup
                payload.PickUpDays = 2;
                ['PickUpRooms', 'PickUpADR', 'PickUpRev', 'PickUpOcc'].forEach(k => payload[k] = true);
                break;
              case '3': // vs 7 Days Pickup
                payload.PickUpDays = 3;
                ['PickUpRooms', 'PickUpADR', 'PickUpRev', 'PickUpOcc'].forEach(k => payload[k] = true);
                break;
              case '4': // vs 14 Days Pickup
                payload.PickUpDays = 4;
                ['PickUpRooms', 'PickUpADR', 'PickUpRev', 'PickUpOcc'].forEach(k => payload[k] = true);
                break;
              case '5': // vs 30 Days Pickup
                payload.PickUpDays = 5;
                ['PickUpRooms', 'PickUpADR', 'PickUpRev', 'PickUpOcc'].forEach(k => payload[k] = true);
                break;
              case '6': // Vs Budget
                ['BudgetRoomsSold', 'BudgetOcc', 'BudgetAdr', 'BudgetRoomRev', 'BudgetRevPar'].forEach(k => payload[k] = true);
                break;
              case '7': // Vs LY
                ['LYRoomsSold', 'LYOccupancy', 'LYADR', 'LYRoomRev', 'LYRevPar'].forEach(k => payload[k] = true);
                break;
              case '8': // Vs LY var
                ['LYRoomsSoldVariance', 'LYADRVariance', 'LYRoomRevenueVariance', 'LYOccVariance'].forEach(k => payload[k] = true);
                break;
              case '9': // Vs Budget var
                ['RoomsSoldVariance', 'ADRVariance', 'RoomRevenueVariance', 'OccVariance'].forEach(k => payload[k] = true);
                break;
              default:
                break;
            }
          } else if (viewby && (viewby === 'DayWise' || viewby === 'PickupDays')) {
            payload.TotalRooms = true;

            if (pickupdates) {

              payload.PickUpDays = parseInt(pickupdates, 10);
            }
            if (pickupof) {
              if (pickupof.includes('RoomsSold')) {
                payload.PickUpRooms = true;
                payload.OTBRoomsSold = true;
              }
              if (pickupof.includes('Occupancy')) {
                payload.PickUpOcc = true;
                payload.OTBOcc = true;
              }
              if (pickupof.includes('ADR')) {
                payload.PickUpADR = true;
                payload.OTBADR = true;
              }
              if (pickupof.includes('RoomRevenue')) {
                payload.PickUpRev = true;
                payload.OTBRoomRev = true;
              }
              if (pickupof.includes('RevPAR')) {
                payload.PickUpRevPar = true;
                payload.OTBRevPar = true;
              }
            }
          } else if (column) {
            // ENABLE based on selected column (Actual always ON)
            switch (column) {
              case 'Occupancy':
                payload.OTBOcc = true;
                if (pickup) payload.PickUpOcc = true;
                if (budget) payload.BudgetOcc = true;
                if (ly) payload.LYOccupancy = true;
                break;

              case 'ADR':
                payload.OTBADR = true;
                if (pickup) payload.PickUpADR = true;
                if (budget) payload.BudgetAdr = true;
                if (ly) payload.LYADR = true;
                break;

              case 'RevPAR':
                payload.OTBRevPar = true;
                if (budget) payload.BudgetRevPar = true;
                if (ly) payload.LYRevPar = true;
                break;

              case 'RoomRevenue':
                payload.OTBRoomRev = true;
                if (budget) payload.BudgetRoomRev = true;
                if (ly) payload.LYRoomRev = true;
                if (pickup) payload.PickUpRev = true;
                break;

              case 'TransRooms':
                payload.TrnsRooms = true;
                break;
              case 'GrpRooms':
                payload.GrpRooms = true;
                break;

              case 'RoomsSold':
                payload.OTBRoomsSold = true;
                if (pickup) payload.PickUpRooms = true;
                if (budget) payload.BudgetRoomsSold = true;
                if (ly) payload.LYRoomsSold = true;
                break;

              default:
                setError("404 Error : Page Not Found");
                return;
            }
          } else if (memorized) {
            if (memorized.CustomizeString) {
              Object.keys(payload).forEach(key => {
                if (typeof payload[key] === 'boolean') {
                  payload[key] = memorized.CustomizeString.toLowerCase().includes(`%${key.toLowerCase()}%`);
                }
              });
            }
          } else {
            setError("404 Error : Page Not Found");
            return;
          }
          const response = await getOTBReport(payload);
          let data = [];
          let totals = null;
          if (Array.isArray(response)) {
            data = response;
            if (response.length > 0) totals = response[0];
          } else if (response && typeof response === 'object') {
            if (Array.isArray(response.OTBReportList)) {
              data = response.OTBReportList;
            } else {
              const key = Object.keys(response).find(k => Array.isArray(response[k]));
              if (key) data = response[key];
            }
            totals = response;
          }

          const pickupLabels = { 1: "1day", 2: "3days", 3: "7days", 4: "14days", 5: "30days" };
          let pickupDurationLabel = 'N/A';

          if (pickup) {
            pickupDurationLabel = pickupLabels[pickup] || `${pickup} Days`;
          } else if (reportType && ['1', '2', '3', '4', '5'].includes(reportType)) {
            const val = parseInt(reportType, 10);
            pickupDurationLabel = pickupLabels[val] || `${val} Days`;
          } else if (pickupdates) {
            const val = parseInt(pickupdates, 10);
            pickupDurationLabel = pickupLabels[val] || `${val} Days`;
          } else if (memorized && memorized.PickUpdays) {
            pickupDurationLabel = pickupLabels[memorized.PickUpdays] || `${memorized.PickUpdays} Days`;
          }

          let displaySettings = { textFormat: 'default', negativeFormat: 'normal', showRedNegative: false, withoutCents: false, excludeZero: false };
          if (memorized) {
            displaySettings.showRedNegative = memorized.InRed;
            displaySettings.withoutCents = memorized.WOCents;
            displaySettings.excludeZero = memorized.ExcludeZero;
            displaySettings.textFormat = memorized.DisplayTextFormat === 2 ? 'upper' : (memorized.DisplayTextFormat === 1 ? 'lower' : 'default');
            displaySettings.negativeFormat = memorized.ShowNegFig === 1 ? 'parentheses' : (memorized.ShowNegFig === 2 ? 'trailing' : 'normal');
          }

          setStateData({
            reportData: data || [],
            metaData: payload,
            totals: totals,
            filters: {
              pickupDuration: pickupDurationLabel,
              viewTransactionRange: viewTransactionRange,
              selectedRows: selectedRows
            },
            dateRange: [payload.ShortFromDate, payload.ShortToDate],
            reportPayload: payload,
            displaySettings: displaySettings,
            showLegalName: showLegalNameVal
          });
        } catch (error) {
          console.error("Error fetching report from URL:", error);
          setError("Failed to load report. Please check your link or try again.");
        } finally {
          setLoading(false);
        }
      };
      fetchReportFromUrl();
    }
  }, [location.search, location.state]);

  const handleRefresh = async (payload) => {
    const currentPayload = payload || stateData?.metaData || stateData?.reportPayload;
    if (currentPayload) {
      setLoading(true);
      try {
        const response = await getOTBReport(currentPayload);
        let data = [];
        let totals = null;
        if (Array.isArray(response)) {
          data = response;
          if (response.length > 0) totals = response[0];
        } else if (response && typeof response === 'object') {
          if (Array.isArray(response.OTBReportList)) {
            data = response.OTBReportList;
          } else {
            const key = Object.keys(response).find(k => Array.isArray(response[k]));
            if (key) data = response[key];
          }
          totals = response;
        }
        setStateData((prev) => ({ ...prev, reportData: data || [], totals: totals }));
      } catch (error) {
        console.error("Error refreshing report:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSettingsChange = (newSettings) => {
    setStateData((prev) => ({
      ...prev,
      displaySettings: { ...prev.displaySettings, ...newSettings }
    }));
  };

  if (loading) return <Backdrop sx={{ color: COLORS.bgWhite, zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}><CircularProgress color="inherit" /></Backdrop>;

  if (error) return (
    <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
      <Alert severity="error">{error}</Alert>
    </Box>
  );

  if (!stateData) return null;

  const { reportData, metaData, filters, dateRange, reportPayload, displaySettings, totals, showLegalName } = stateData;
  const isUrlMode = !location.state;

  return (
    <Stack height="100vh" overflow="hidden">
      {!isUrlMode && (
        <Buttons filters={filters} dateRange={dateRange} reportPayload={reportPayload} displaySettings={displaySettings} data={reportData} metaData={metaData} onRefresh={handleRefresh} onSettingsChange={handleSettingsChange} showLegalName={showLegalName} totals={totals} />
      )}
      <Box flex={1} overflow="hidden" sx={{ display: 'flex', flexDirection: 'column' }}>
        <Tables reportData={metaData} data={reportData} pickupDaysLabel={filters?.pickupDuration} displaySettings={displaySettings} selectedRows={filters?.selectedRows} showLegalName={showLegalName} />
        <Box flex={1} overflow="hidden">
          <OTBReport reportData={reportData} reportPayload={reportPayload} pickupDaysLabel={filters?.pickupDuration} displaySettings={displaySettings} selectedRows={filters?.selectedRows} totals={totals} showLegalName={showLegalName} />
        </Box>
      </Box>
    </Stack>
  );
};

export default ReportFinalPage;
